import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiUser, FiPhone, FiTrash2, FiPlus, FiSearch, FiLock } from 'react-icons/fi';
import { auth as firebaseAuth } from '../firebase/config';
import SEO from './layout/SEO';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ContactBook = ({ user, session }) => {
  const [contacts, setContacts] = useState([]);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const getHeaders = async () => {
    if (user?.isGuest) return { 'x-session-id': session };
    const token = await firebaseAuth.currentUser?.getIdToken();
    return { 'Authorization': `Bearer ${token}` };
  };

  const fetchContacts = async () => {
    if (user?.isGuest) return; // Guests can't have contacts
    setLoading(true);
    try {
      const headers = await getHeaders();
      const res = await axios.get(`${API_URL}/contacts`, { headers });
      setContacts(res.data);
    } catch (err) {
      console.error('Fetch contacts error', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
  }, [user]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (user?.isGuest) return;
    setError('');
    try {
      const headers = await getHeaders();
      await axios.post(`${API_URL}/contacts`, { name, phone }, { headers });
      setName('');
      setPhone('');
      fetchContacts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add contact');
    }
  };

  const handleDelete = async (id) => {
    try {
      const headers = await getHeaders();
      await axios.delete(`${API_URL}/contacts/${id}`, { headers });
      setContacts(contacts.filter(c => c._id !== id));
    } catch (err) {
      console.error('Delete contact error', err);
    }
  };

  const filteredContacts = contacts.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.phone.includes(search)
  );

  return (
    <div className="animate-fade">
      <SEO
        title="Save Contacts for Quick Calling"
        description="Manage your international contacts for quick and easy dialing."
        canonical="/contacts"
      />
      <h1>Contacts</h1>

      {user?.isGuest ? (
        <div className="card" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
          <FiLock size={48} color="var(--primary)" style={{ marginBottom: '1rem' }} />
          <h2>Members Only</h2>
          <p>Sign in to save and manage your contacts permanently.</p>
          <button className="primary" style={{ marginTop: '1.5rem' }} onClick={() => window.location.href = '/login'}>
            Sign In Now
          </button>
        </div>
      ) : (
        <>
          <div className="card" style={{ marginBottom: '2rem' }}>
            <form onSubmit={handleAdd} style={{ display: 'flex', gap: '1rem' }}>
              <div style={{ flex: 2, position: 'relative' }}>
                <FiUser style={{ position: 'absolute', left: '12px', top: '14px', color: 'var(--text-muted)' }} />
                <input
                  placeholder="Name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  style={{ paddingLeft: '2.5rem', marginBottom: 0 }}
                  required
                />
              </div>
              <div style={{ flex: 2, position: 'relative' }}>
                <FiPhone style={{ position: 'absolute', left: '12px', top: '14px', color: 'var(--text-muted)' }} />
                <input
                  placeholder="+123456789"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  style={{ paddingLeft: '2.5rem', marginBottom: 0 }}
                  required
                />
              </div>
              <button className="primary" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                <FiPlus /> Add
              </button>
            </form>
            {error && <p style={{ color: 'var(--danger)', marginTop: '0.5rem', fontSize: '0.875rem' }}>{error}</p>}
          </div>

          <div className="card">
            <div style={{ position: 'relative', marginBottom: '1.5rem' }}>
              <FiSearch style={{ position: 'absolute', left: '12px', top: '12px', color: 'var(--text-muted)' }} />
              <input
                placeholder="Search contacts..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ paddingLeft: '2.5rem', background: 'transparent', marginBottom: 0 }}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {filteredContacts.length === 0 ? (
                <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>No contacts found.</p>
              ) : (
                filteredContacts.map(contact => (
                  <div key={contact._id} style={{ display: 'flex', alignItems: 'center', padding: '0.75rem 1rem', background: '#0f172a', borderRadius: '0.75rem', border: '1px solid var(--border)' }}>
                    <div style={{ width: '40px', height: '40px', background: 'var(--primary)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '1rem', fontWeight: 700 }}>
                      {contact.name[0].toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontWeight: 600 }}>{contact.name}</p>
                      <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>{contact.phone}</p>
                    </div>
                    <button
                      onClick={() => handleDelete(contact._id)}
                      style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', padding: '8px' }}
                    >
                      <FiTrash2 size={20} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ContactBook;